import { parseMoneyToCents, tt, type MoneyInput } from '@agos/shared'

/**
 * The loyalty programme's tiers, and the two places they are configured from.
 *
 * **FB-097.** `/loyalty-program` has two editors for one thing. `LoyaltyLevelsEditor` writes
 * `loyalty_levels` — key, name, emoji, colour, `min_orders`, `multiplier`. The *Body za vernosť*
 * block below it writes eight columns on `loyalty_settings`: `silver_threshold`, `gold_threshold`,
 * `gold_master_threshold`, `elite_master_threshold` and the four matching multipliers. Same five
 * tiers, same numbers, two save buttons.
 *
 * Only one of them decides anything:
 *
 * ```java
 * // CustomerLoyalty.tierFor
 * if (customTiers.isEmpty()) {
 *   String key = orderCount >= settings.eliteMasterThreshold() ? "elite_master" : … ;
 *   return new Tier(null, key, settings.multiplierFor(key));
 * }
 * CustomTier picked = customTiers.get(0);
 * for (CustomTier candidate : customTiers) {
 *   if (orderCount >= candidate.minOrders()) picked = candidate;
 * }
 * ```
 *
 * `customTiers` is `SELECT key, min_orders, multiplier FROM loyalty_levels WHERE active = true`, and
 * it has five rows. `LoyaltyLevelService.backfillCustomerLevels` reads the same table. So the eight
 * `loyalty_settings` columns are a fallback for a franchise that predates the tier editor — and this
 * franchise does not. Raise the Gold threshold from 100 to 60 in the *Body za vernosť* block, press
 * save, get *"Nastavenia bodov uložené"*, and nobody moves.
 *
 * Nobody has noticed because the two agree today, digit for digit: 50/100/150/200 with
 * 1,00/1,20/1,40/1,70 in `loyalty_settings`, and 0/50/100/150/200 with 1,00/1,00/1,20/1,40/1,70 in
 * `loyalty_levels`. The moment one is edited they diverge in silence.
 *
 * **FB-098.** Four of the five tiers are out of reach. 1 108 loyalty members: 1 106 on Bronze, one on
 * Silver with 53 orders, one on Gold Master with 184. Nobody on Gold, nobody on Elite Master. The
 * busiest Bronze customer has **44** orders against a Silver threshold of 50. So the multipliers
 * above 1,0 have applied to two customers in the programme's life, and the page that sets them says
 * nothing about it.
 */

// ─── the tiers as the server has them ───────────────────────────────────────

export interface Tier {
  id?: number
  key?: string
  name?: string
  emoji?: string | null
  color?: string | null
  minOrders?: number
  multiplier?: MoneyInput
  sortOrder?: number
  active?: boolean
  isStarter?: boolean
}

/** The five keys the `loyalty_level` enum column accepts. A custom tier maps onto the nearest. */
export const FIXED_KEYS = ['bronze', 'silver', 'gold', 'gold_master', 'elite_master'] as const

export type FixedKey = (typeof FIXED_KEYS)[number]

/** Weakest first — the order the server resolves in, and the order they are shown. */
export function byThreshold(tiers: Tier[]): Tier[] {
  return [...tiers].sort((left, right) => {
    const byOrders = (left.minOrders ?? 0) - (right.minOrders ?? 0)
    if (byOrders !== 0) return byOrders
    const bySort = (left.sortOrder ?? 0) - (right.sortOrder ?? 0)
    if (bySort !== 0) return bySort
    return (left.id ?? 0) - (right.id ?? 0)
  })
}

export function activeTiers(tiers: Tier[]): Tier[] {
  return byThreshold(tiers.filter((tier) => tier.active !== false))
}

/**
 * The tier a customer with this many orders earns — `CustomerLoyalty.tierFor`, exactly.
 *
 * The last tier whose threshold is met, walking upwards, with the weakest as the floor. Ported so
 * the page can say who a threshold change would actually move, which is the only way to tell that
 * FB-097's inert fields are inert.
 */
export function tierFor(orderCount: number, tiers: Tier[]): Tier | null {
  const ladder = activeTiers(tiers)
  if (ladder.length === 0) return null
  let picked = ladder[0] as Tier
  for (const candidate of ladder) {
    if (orderCount >= (candidate.minOrders ?? 0)) picked = candidate
  }
  return picked
}

// ─── does the settings block still matter? ──────────────────────────────────

export interface PointsSettings {
  pointsPerEuro?: MoneyInput
  minRedeemPoints?: number
  /** §10A.217 - on `PUBLIC_FIELDS` too, because the checkout has to be able to say so. */
  combinePointsWithDiscounts?: boolean
  pointValue?: MoneyInput
  silverThreshold?: number
  goldThreshold?: number
  goldMasterThreshold?: number
  eliteMasterThreshold?: number
  silverMultiplier?: MoneyInput
  goldMultiplier?: MoneyInput
  goldMasterMultiplier?: MoneyInput
  eliteMasterMultiplier?: MoneyInput
}

export interface ProgramSettings extends PointsSettings {
  isActive?: boolean
  programName?: string
  description?: string
  welcomeMessage?: string
  labelBronze?: string
  labelSilver?: string
  labelGold?: string
  labelGoldMaster?: string
  labelEliteMaster?: string
  enabledSilver?: boolean
  enabledGold?: boolean
  enabledGoldMaster?: boolean
  enabledEliteMaster?: boolean
  /* §9.565 - answered all along and read by nothing until FB-854. */
  referralEnabled?: boolean
  referralDiscountType?: string
  referralDiscountValue?: MoneyInput
  referralRewardPoints?: number
}

/**
 * Whether the eight `loyalty_settings` tier columns decide anything.
 *
 * They do only when no active row exists in `loyalty_levels` — `if (customTiers.isEmpty())`. So this
 * is the sentence the page needs before it shows those fields at all.
 */
export function settingsTiersAreLive(tiers: Tier[]): boolean {
  return activeTiers(tiers).length === 0
}

export interface Divergence {
  key: FixedKey
  /** What `loyalty_settings` says. */
  settingsThreshold: number
  settingsMultiplier: number
  /** What `loyalty_levels` says — the one that counts. */
  tierThreshold: number | null
  tierMultiplier: number | null
}

/**
 * The threshold and multiplier columns, keyed by tier.
 *
 * **Typed by the keys they actually hold, not `keyof PointsSettings`.** They were the latter, so
 * `settings[SETTINGS_MULTIPLIER[key]]` read as *any value in that interface* - and the moment
 * §10A.217 added a `boolean` member the compiler said so. `Number(true)` is 1, which is the shape
 * of the bug a wide index invites: it would have compiled and read a multiplier of 1 off a switch.
 */
type ThresholdKey = Extract<keyof PointsSettings, `${string}Threshold`>
type MultiplierKey = Extract<keyof PointsSettings, `${string}Multiplier`>

const SETTINGS_THRESHOLD: Record<Exclude<FixedKey, 'bronze'>, ThresholdKey> = {
  silver: 'silverThreshold',
  gold: 'goldThreshold',
  gold_master: 'goldMasterThreshold',
  elite_master: 'eliteMasterThreshold',
}

const SETTINGS_MULTIPLIER: Record<Exclude<FixedKey, 'bronze'>, MultiplierKey> = {
  silver: 'silverMultiplier',
  gold: 'goldMultiplier',
  gold_master: 'goldMasterMultiplier',
  elite_master: 'eliteMasterMultiplier',
}

/**
 * Where the two configurations disagree, tier by tier.
 *
 * They agree today. This exists so that the first divergence is visible on the screen rather than in
 * a customer's points balance — and so the page can prove which side won.
 */
export function divergences(settings: ProgramSettings, tiers: Tier[]): Divergence[] {
  const ladder = activeTiers(tiers)
  const found: Divergence[] = []

  for (const key of ['silver', 'gold', 'gold_master', 'elite_master'] as const) {
    const tier = ladder.find((candidate) => candidate.key === key)
    const settingsThreshold = Number(settings[SETTINGS_THRESHOLD[key]] ?? 0)
    const settingsMultiplier = numberOf(settings[SETTINGS_MULTIPLIER[key]])
    const tierThreshold = tier === undefined ? null : (tier.minOrders ?? 0)
    const tierMultiplier = tier === undefined ? null : numberOf(tier.multiplier)

    if (tierThreshold !== settingsThreshold || tierMultiplier !== settingsMultiplier) {
      found.push({ key, settingsThreshold, settingsMultiplier, tierThreshold, tierMultiplier })
    }
  }

  return found
}

function numberOf(value: MoneyInput): number {
  if (value === null || value === undefined || value === '') return 0
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}

export { numberOf as rateOf }

// ─── who is actually on each tier ───────────────────────────────────────────

export interface TierPopulation {
  key: string
  name: string
  minOrders: number
  members: number
  /** The busiest customer who has not reached the next tier up. */
  highestBelow: number | null
  reachable: boolean
}

export interface Distribution {
  members: number
  tiers: TierPopulation[]
  /** Tiers nobody has ever reached. */
  emptyTiers: string[]
  /** How many orders the closest customer still needs for the first empty tier. */
  shortfall: number | null
}

/**
 * How the members are spread across the ladder, and which rungs nobody stands on.
 *
 * **FB-098.** Counted from the customers the page already has, because the server offers no such
 * figure — the rule FB-060 set: derived, and labelled as derived. A settings page that lets somebody
 * set a 1,7× multiplier on a tier two customers have ever reached should say so.
 */
export function distribution(
  orderCounts: number[],
  tiers: Tier[],
): Distribution {
  const ladder = activeTiers(tiers)
  const counts = new Map<string, number>()
  const highest = new Map<string, number>()

  for (const orderCount of orderCounts) {
    const tier = tierFor(orderCount, ladder)
    if (tier?.key === undefined) continue
    counts.set(tier.key, (counts.get(tier.key) ?? 0) + 1)
    highest.set(tier.key, Math.max(highest.get(tier.key) ?? 0, orderCount))
  }

  const populations: TierPopulation[] = ladder.map((tier) => ({
    key: tier.key ?? '',
    name: tier.name ?? tier.key ?? '',
    minOrders: tier.minOrders ?? 0,
    members: counts.get(tier.key ?? '') ?? 0,
    highestBelow: highest.get(tier.key ?? '') ?? null,
    reachable: (counts.get(tier.key ?? '') ?? 0) > 0,
  }))

  const emptyTiers = populations.filter((tier) => tier.members === 0).map((tier) => tier.key)
  const firstEmpty = populations.find((tier) => tier.members === 0)
  const busiest = orderCounts.length === 0 ? 0 : Math.max(...orderCounts)
  const shortfall =
    firstEmpty === undefined ? null : Math.max(0, firstEmpty.minOrders - busiest)

  return { members: orderCounts.length, tiers: populations, emptyTiers, shortfall }
}

/** How many customers a threshold change would move, up or down. */
export function movedBy(orderCounts: number[], before: Tier[], after: Tier[]): number {
  let moved = 0
  for (const orderCount of orderCounts) {
    if (tierFor(orderCount, before)?.key !== tierFor(orderCount, after)?.key) moved += 1
  }
  return moved
}

// ─── the tier editor's form ─────────────────────────────────────────────────

export interface TierForm {
  key: string
  name: string
  emoji: string
  color: string
  minOrders: string
  multiplier: string
  active: boolean
  isStarter: boolean
}

export const EMOJI_PALETTE = [
  '🏆', '🥉', '🥈', '🥇', '👑', '💎', '⭐', '🌟', '🔥', '⚡', '🚀', '🎖️',
  '🏅', '💫', '✨', '🎯', '🦄', '🍕', '🍔', '☕', '🍷', '🌹', '💝', '🎁',
] as const

export function tierFormOf(tier: Tier): TierForm {
  return {
    key: tier.key ?? '',
    name: tier.name ?? '',
    emoji: tier.emoji ?? '🏆',
    color: tier.color ?? '#f59e0b',
    minOrders: String(tier.minOrders ?? 0),
    multiplier: String(numberOf(tier.multiplier) || 1),
    active: tier.active !== false,
    isStarter: tier.isStarter === true,
  }
}

export function emptyTierForm(): TierForm {
  return {
    key: '',
    name: '',
    emoji: '🏆',
    color: '#f59e0b',
    minOrders: '0',
    multiplier: '1',
    active: true,
    isStarter: false,
  }
}

export type TierProblem =
  | { kind: 'no-key' }
  | { kind: 'bad-key'; value: string }
  | { kind: 'duplicate-key'; value: string }
  | { kind: 'no-name' }
  | { kind: 'threshold-not-whole' }
  | { kind: 'threshold-negative' }
  | { kind: 'duplicate-threshold'; other: string }
  | { kind: 'multiplier-below-one' }
  | { kind: 'multiplier-absurd'; value: number }
  | { kind: 'starter-not-zero' }

/** A key has to survive being written into SQL and compared against the enum's five names. */
const KEY_PATTERN = /^[a-z][a-z0-9_]*$/

/**
 * What is wrong with a tier before it is saved.
 *
 * Legacy validates none of this. Two facts make it matter: the key is what
 * `customers.custom_level_key` stores and what `CustomerLoyalty.noDemotion` compares, so a renamed
 * key orphans everybody who held it; and two tiers on the same threshold make the resolution order
 * decide silently which one somebody lands on.
 *
 * A multiplier below 1 is refused, because it means a customer earns *fewer* points for climbing.
 */
export function tierProblems(form: TierForm, others: Tier[]): TierProblem[] {
  const problems: TierProblem[] = []

  const key = form.key.trim()
  if (key === '') problems.push({ kind: 'no-key' })
  else if (!KEY_PATTERN.test(key)) problems.push({ kind: 'bad-key', value: key })
  else if (others.some((tier) => tier.key === key)) {
    problems.push({ kind: 'duplicate-key', value: key })
  }

  if (form.name.trim() === '') problems.push({ kind: 'no-name' })

  const threshold = Number(form.minOrders.trim())
  if (!Number.isFinite(threshold) || !Number.isInteger(threshold)) {
    problems.push({ kind: 'threshold-not-whole' })
  } else if (threshold < 0) {
    problems.push({ kind: 'threshold-negative' })
  } else {
    const clash = others.find(
      (tier) => tier.active !== false && (tier.minOrders ?? 0) === threshold,
    )
    if (clash !== undefined) {
      problems.push({ kind: 'duplicate-threshold', other: clash.name ?? clash.key ?? '' })
    }
  }

  if (form.isStarter && threshold !== 0) problems.push({ kind: 'starter-not-zero' })

  const multiplier = numberOf(form.multiplier)
  if (multiplier < 1) problems.push({ kind: 'multiplier-below-one' })
  else if (multiplier > 10) problems.push({ kind: 'multiplier-absurd', value: multiplier })

  return problems
}

export function canSaveTier(problems: TierProblem[]): boolean {
  return !problems.some((problem) => problem.kind !== 'multiplier-absurd')
}

export function tierPayload(form: TierForm): Record<string, unknown> {
  return {
    key: form.key.trim(),
    name: form.name.trim(),
    emoji: form.emoji,
    color: form.color,
    minOrders: Number(form.minOrders.trim()),
    multiplier: numberOf(form.multiplier),
    active: form.active,
    isStarter: form.isStarter,
  }
}

/** The tiers as they would be after this edit, for counting who moves. */
export function withEditedTier(tiers: Tier[], form: TierForm, editingId: number | null): Tier[] {
  const edited: Tier = {
    id: editingId ?? -1,
    key: form.key.trim(),
    name: form.name.trim(),
    minOrders: Number(form.minOrders.trim()) || 0,
    multiplier: numberOf(form.multiplier),
    active: form.active,
    isStarter: form.isStarter,
  }
  if (editingId === null) return [...tiers, edited]
  return tiers.map((tier) => (tier.id === editingId ? { ...tier, ...edited } : tier))
}

// ─── the programme's own settings ───────────────────────────────────────────

export interface ProgramForm {
  isActive: boolean
  programName: string
  description: string
  welcomeMessage: string
  pointsPerEuro: string
  minRedeemPoints: string
  pointValue: string
  /**
   * Whether points may be spent on an order that already has a zľava - `BACKEND.md` §10A.217.
   *
   * The owner's own label: *"Kombinovať vernostné body zo zľavami"*. Off, the customer site refuses
   * the combination and **does not spend the points**, so a 50 % coupon plus points worth the other
   * half stops being an order for nothing.
   */
  combinePointsWithDiscounts: boolean
}

export function programFormOf(settings: ProgramSettings): ProgramForm {
  return {
    isActive: settings.isActive === true,
    programName: settings.programName ?? '',
    description: settings.description ?? '',
    welcomeMessage: settings.welcomeMessage ?? '',
    pointsPerEuro: String(numberOf(settings.pointsPerEuro) || 1),
    minRedeemPoints: String(settings.minRedeemPoints ?? 100),
    pointValue: String(numberOf(settings.pointValue) || 0.01),
    /* §10A.217 - **`!== false`, not `=== true`.** The column is `NOT NULL DEFAULT true`, so the
     * server always sends a boolean; but a franchise read before `V62` reached its database answers
     * `undefined`, and defaulting that to *off* would show the screen refusing a combination the
     * server is still allowing. Every other boolean here reads `=== true` because *absent* means
     * *not switched on*; this one ships on. */
    combinePointsWithDiscounts: settings.combinePointsWithDiscounts !== false,
  }
}

export type ProgramProblem =
  | { kind: 'no-name' }
  | { kind: 'points-per-euro-zero' }
  | { kind: 'point-value-zero' }
  | { kind: 'redeem-not-whole' }
  | { kind: 'redeem-negative' }

export function programProblems(form: ProgramForm): ProgramProblem[] {
  const problems: ProgramProblem[] = []
  if (form.programName.trim() === '') problems.push({ kind: 'no-name' })
  if (numberOf(form.pointsPerEuro) <= 0) problems.push({ kind: 'points-per-euro-zero' })
  if (numberOf(form.pointValue) <= 0) problems.push({ kind: 'point-value-zero' })

  const redeem = Number(form.minRedeemPoints.trim())
  if (!Number.isFinite(redeem) || !Number.isInteger(redeem)) problems.push({ kind: 'redeem-not-whole' })
  else if (redeem < 0) problems.push({ kind: 'redeem-negative' })

  return problems
}

export function canSaveProgram(problems: ProgramProblem[]): boolean {
  return problems.length === 0
}

export function programPayload(form: ProgramForm): Record<string, unknown> {
  return {
    isActive: form.isActive,
    programName: form.programName.trim(),
    description: form.description.trim(),
    welcomeMessage: form.welcomeMessage.trim(),
    pointsPerEuro: numberOf(form.pointsPerEuro),
    minRedeemPoints: Number(form.minRedeemPoints.trim()) || 0,
    pointValue: numberOf(form.pointValue),
    combinePointsWithDiscounts: form.combinePointsWithDiscounts,
  }
}

/**
 * What the minimum redemption is worth, and what it takes to earn.
 *
 * 100 points at 0,01 € is 1,00 € off, earned by spending 100 € at one point per euro. Legacy shows
 * the three numbers as separate fields and never multiplies them, so the only way to know what the
 * programme actually offers is to do it by hand.
 */
export interface RedemptionWorth {
  minRedeemPoints: number
  valueCents: number
  eurosToEarn: number
  /** The discount as a share of what was spent to earn it, in basis points. */
  effectiveRateBasisPoints: number
}

export function redemptionWorth(form: ProgramForm): RedemptionWorth {
  const points = Number(form.minRedeemPoints.trim()) || 0
  const pointValue = numberOf(form.pointValue)
  const perEuro = numberOf(form.pointsPerEuro)
  const valueCents = Math.round(points * pointValue * 100)
  const eurosToEarn = perEuro > 0 ? points / perEuro : 0
  const spentCents = Math.round(eurosToEarn * 100)
  return {
    minRedeemPoints: points,
    valueCents,
    eurosToEarn,
    effectiveRateBasisPoints: spentCents > 0 ? Math.round((valueCents / spentCents) * 10_000) : 0,
  }
}

/** `170` → `"1,7×"`. A multiplier, not money — it keeps its decimals. */
export function formatMultiplier(value: MoneyInput): string {
  const parsed = numberOf(value)
  return `${String(Math.round(parsed * 100) / 100).replace('.', ',')}×`
}

export function formatRate(basisPoints: number): string {
  return `${String(Math.round(basisPoints) / 100).replace('.', ',')} %`
}

// `tierLabel` was here and is deleted — §9.35, 2026-08-06. It joined the emoji and the name into one
// string; the screen draws them as two elements, which is why it was never called — a `<span>` for
// the emoji and a truncating `<p>` for the name lays out better than a concatenation, and falls back
// to 🏆 where this fell back to an empty string. Superseded rather than unused, and a helper that
// formats a row worse than the row does is a trap for the next screen that needs one.

export function starterOf(tiers: Tier[]): Tier | null {
  const ladder = activeTiers(tiers)
  return ladder.find((tier) => tier.isStarter === true) ?? ladder[0] ?? null
}

/** A tier nobody can be demoted out of, so deleting it orphans whoever holds it. */
export function holdersOf(key: string, orderCounts: number[], tiers: Tier[]): number {
  let held = 0
  for (const orderCount of orderCounts) {
    if (tierFor(orderCount, tiers)?.key === key) held += 1
  }
  return held
}

export function deleteWarning(tier: Tier, holders: number): string {
  if (tier.isStarter === true) return tt('Základnú úroveň sa zmazať nedá.')
  if (holders > 0) {
    return tt('Túto úroveň drží niekoľko zákazníkov - po zmazaní ich treba prepočítať.')
  }
  return tt('Túto úroveň nikto nedrží.')
}

// ─── the referral programme, §9.565 ─────────────────────────────────────────

/**
 * What a referral is worth, and to whom - FB-854, §9.565.
 *
 * **`WebOrderDiscounts` has been discounting referred web orders by `referral_discount_value` the whole
 * time and no screen could change it.** Switched on at both franchises at 10 % with 50 reward points, and
 * legacy's own panel for it sits on the page AGOS ported. The server's field map already carried all four
 * keys, so this is a screen for a write path that was always open.
 */
export const REFERRAL_DISCOUNT_TYPES = ['percent', 'euro'] as const

export type ReferralDiscountType = (typeof REFERRAL_DISCOUNT_TYPES)[number]

/**
 * The largest value each type may carry, read off legacy's own two `max` attributes.
 *
 * **The type decides the bound because it decides the meaning.** A `percent` over 100 pays the customer
 * to order; the same figure against `euro` is an ordinary discount on a large basket. Legacy writes
 * `max={... === "percent" ? "100" : "999"}`.
 */
export const REFERRAL_VALUE_CEILING: Record<ReferralDiscountType, number> = {
  percent: 100,
  euro: 999,
}

export interface ReferralForm {
  enabled: boolean
  discountType: ReferralDiscountType
  discountValue: string
  rewardPoints: string
}

/** The type as one of the two the server's own readers understand, defaulting as they do. */
export function referralTypeOf(raw: string | undefined): ReferralDiscountType {
  return raw === 'euro' ? 'euro' : 'percent'
}

export function referralFormOf(settings: ProgramSettings): ReferralForm {
  return {
    /* `=== true`, unlike §10A.217's `combinePointsWithDiscounts`: that column is `NOT NULL DEFAULT
     * true` so an absent answer means an old server, while this one is off until a franchise says
     * otherwise - so *absent* reads as *not switched on*. Reading it the other way would show the
     * panel as live on a franchise that has never had the programme. */
    enabled: settings.referralEnabled === true,
    discountType: referralTypeOf(settings.referralDiscountType),
    /* `|| 10` matches `WebOrderDiscounts`' own fallback of `BigDecimal.TEN`, so the box shows the
     * figure the server would actually apply rather than a nought it never uses. */
    discountValue: String(numberOf(settings.referralDiscountValue) || 10),
    rewardPoints: String(settings.referralRewardPoints ?? 50),
  }
}

export type ReferralProblem =
  | { kind: 'value-blank' }
  | { kind: 'value-not-positive' }
  | { kind: 'value-over-ceiling'; ceiling: number }
  | { kind: 'points-not-whole' }
  | { kind: 'points-negative' }

/**
 * **Only checked while the programme is ON.**
 *
 * A franchise switching the referral programme off must not have to satisfy the value validator to do
 * it: an invalid figure behind a switched-off programme reaches no customer, and refusing the save there
 * would leave an operator unable to turn off the thing that is costing them money.
 */
export function referralProblems(form: ReferralForm): ReferralProblem[] {
  if (!form.enabled) return []
  const problems: ReferralProblem[] = []

  /* **Blank is refused, not parsed.** `numberOf('')` is 0 and so is `Number('')`, so a cleared box
   * would otherwise save a referral worth nought under a success toast - FB-838's shape on a discount,
   * and `agos-an-empty-string-parses-as-zero`. */
  if (form.discountValue.trim() === '') problems.push({ kind: 'value-blank' })
  else {
    const value = numberOf(form.discountValue)
    const ceiling = REFERRAL_VALUE_CEILING[form.discountType]
    if (value <= 0) problems.push({ kind: 'value-not-positive' })
    else if (value > ceiling) problems.push({ kind: 'value-over-ceiling', ceiling })
  }

  const points = Number(form.rewardPoints.trim())
  if (!Number.isFinite(points) || !Number.isInteger(points)) problems.push({ kind: 'points-not-whole' })
  else if (points < 0) problems.push({ kind: 'points-negative' })

  return problems
}

export function canSaveReferral(problems: ReferralProblem[]): boolean {
  return problems.length === 0
}

export function referralProblemMessage(problem: ReferralProblem): string {
  switch (problem.kind) {
    case 'value-blank':
      return tt('Zadajte hodnotu odporúčania.')
    case 'value-not-positive':
      return tt('Hodnota musí byť vyššia ako nula - inak je program vypnutý prepínačom.')
    case 'value-over-ceiling':
      return problem.ceiling === 100
        ? tt('Percento nemôže byť vyššie ako 100.')
        : tt('Suma nemôže byť vyššia ako 999 €.')
    case 'points-not-whole':
      return tt('Body musia byť celé číslo.')
    case 'points-negative':
      return tt('Body nemôžu byť negatívne.')
  }
}

/**
 * Its own body, not four more keys on {@link programPayload}.
 *
 * The referral panel is its own card with its own button, as in legacy, and every partial body this
 * endpoint takes exists so a card can save what it owns. Folding these in would make *save* on the
 * programme card silently rewrite the referral discount.
 *
 * **The points are an `integer` server-side** and deliberately outside `NUMERIC_FIELDS`, so they leave
 * as a JSON number; the value is `numeric` and is in that set.
 */
export function referralPayload(form: ReferralForm): Record<string, unknown> {
  return {
    referralEnabled: form.enabled,
    referralDiscountType: form.discountType,
    referralDiscountValue: numberOf(form.discountValue),
    referralRewardPoints: Number(form.rewardPoints.trim()) || 0,
  }
}

export { parseMoneyToCents }
